import type { Announcement } from "@core/shared/types";
import { URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { ArrowUpLeft, BellRing, BookOpen, Megaphone } from "lucide-react";

export function ModernHomeAnnouncements({
  announcements = [],
}: {
  announcements?: Announcement[];
}) {
  if (announcements.length === 0) return null;

  return (
    <aside className="flex min-h-full flex-col rounded-[2rem] border border-amber-200 bg-amber-50/80 p-5 dark:border-amber-900 dark:bg-amber-950/20 sm:p-6">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-400 text-slate-950 shadow-lg shadow-amber-500/20">
          <BellRing className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-black text-slate-950 dark:text-white">
            {trans("home.importantAnnouncements")}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {trans("home.importantAnnouncementsDescription")}
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {announcements.slice(0, 4).map((announcement, index) => {
          const href = announcement.lesson
            ? URLS.lessons.view(announcement.lesson)
            : announcement.assessment
              ? URLS.assessments.view(announcement.assessment)
              : undefined;
          const content = (
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <div className="flex min-w-0 gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-amber-700 shadow-sm dark:bg-slate-950 dark:text-amber-300">
                  <Megaphone className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <strong className="line-clamp-2 text-sm font-black text-slate-950 dark:text-white">
                    {announcement.title || trans("home.importantAnnouncements")}
                  </strong>
                  {announcement.description ? (
                    <span className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                      {announcement.description}
                    </span>
                  ) : null}
                </span>
                {href ? (
                  <ArrowUpLeft className="h-4 w-4 shrink-0 text-amber-600 ltr:rotate-90 rtl:rotate-0" />
                ) : null}
              </div>

              {announcement.lesson ? (
                <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-100/70 p-2.5 dark:border-amber-900 dark:bg-amber-950/50">
                  {announcement.lesson.image?.url ? (
                    <img
                      src={announcement.lesson.image.url}
                      alt={announcement.lesson.name}
                      className="h-14 w-16 shrink-0 rounded-xl object-cover"
                    />
                  ) : (
                    <span className="flex h-14 w-16 shrink-0 items-center justify-center rounded-xl bg-white text-amber-700 dark:bg-slate-950 dark:text-amber-300">
                      <BookOpen className="h-5 w-5" />
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <strong className="line-clamp-2 block text-xs font-black text-slate-950 dark:text-white">
                      {announcement.lesson.name}
                    </strong>
                    <span className="mt-1 block truncate text-[11px] text-slate-500 dark:text-slate-400">
                      {announcement.lesson.subject?.name ||
                        announcement.lesson.grade?.name}
                    </span>
                  </span>
                  <ArrowUpLeft className="h-4 w-4 shrink-0 text-amber-700 ltr:rotate-90 rtl:rotate-0" />
                </div>
              ) : null}
            </div>
          );

          return href ? (
            <Link
              key={announcement.id}
              to={href}
              className="flex gap-3 rounded-2xl border border-amber-200/70 bg-white/70 p-3 transition hover:bg-white dark:border-amber-900 dark:bg-slate-950/40 dark:hover:bg-slate-950"
            >
              {content}
            </Link>
          ) : (
            <div
              key={`${announcement.id}-${index}`}
              className="flex gap-3 rounded-2xl border border-amber-200/70 bg-white/70 p-3 dark:border-amber-900 dark:bg-slate-950/40"
            >
              {content}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
